/**
 * Build a same-source comparison report from pack + resolved edit.
 * Plain language artifact comparison — no good/bad labels on every choice.
 */
import {
  resolveEdit,
  type EditGraph,
  type ResolvedEdit,
  type SourcePack,
} from "@desk/core";
import { shouldShowReconstructionLabel } from "./disclosure.js";
import type { SourceCompareReport } from "./types.js";

export function buildSourceCompareReport(
  pack: SourcePack,
  graph: EditGraph,
  resolved: ResolvedEdit = resolveEdit(graph, pack),
): SourceCompareReport {
  const entityById = new Map(pack.entities.map((e) => [e.id, e]));
  const claimById = new Map(pack.claims.map((c) => [c.id, c]));
  const tokenById = new Map(pack.captionVocabulary.tokens.map((t) => [t.id, t]));

  const retainedBeats: SourceCompareReport["retainedBeats"] = [];
  const omittedBeats: SourceCompareReport["omittedBeats"] = [];
  const trimmedBeats: SourceCompareReport["trimmedBeats"] = [];

  for (const beat of pack.beats) {
    const cut = resolved.cuts.get(beat.id);
    const mode = cut?.mode ?? "keep";
    const row = { id: beat.id, label: beat.label };
    if (mode === "omit") {
      omittedBeats.push(row);
    } else if (mode === "trim") {
      trimmedBeats.push(row);
      retainedBeats.push(row);
    } else {
      retainedBeats.push(row);
    }
  }

  const canonicalOrder = pack.beats.map((b) => b.id);
  const reordered =
    resolved.beatOrder.length === canonicalOrder.length &&
    resolved.beatOrder.some((id, i) => id !== canonicalOrder[i]);

  const frameExcludedCarriers: SourceCompareReport["frameExcludedCarriers"] = [];
  for (const beat of pack.beats) {
    const cut = resolved.cuts.get(beat.id);
    if (cut?.mode === "omit") continue;
    const frame = resolved.frames.get(beat.id);
    if (!frame) continue;
    const tight =
      frame.crop.wMilli <= 450 || frame.crop.hMilli <= 450 || frame.zoomMilli >= 1600;
    if (!tight) continue;
    for (const entityId of beat.entityIds) {
      const entity = entityById.get(entityId);
      if (!entity?.contextCarrier) continue;
      if (frame.subjectEntityId && frame.subjectEntityId !== entityId) {
        frameExcludedCarriers.push({
          beatId: beat.id,
          beatLabel: beat.label,
          entityId,
          entityLabel: entity.name ?? entityId,
        });
      }
    }
  }

  const implied = new Map<string, "supported" | "unsupported" | "contradicted">();
  const retainedSet = new Set(
    resolved.beatOrder.filter((id) => resolved.cuts.get(id)?.mode !== "omit"),
  );

  for (const caption of resolved.captions) {
    for (const tokenId of caption.tokenIds) {
      const token = tokenById.get(tokenId);
      if (!token) continue;
      for (const claimId of token.claimIds) {
        noteClaim(claimId);
      }
    }
    for (const claim of pack.claims) {
      if (
        claim.implyingTokenGroups?.some((group) =>
          group.every((tid) => caption.tokenIds.includes(tid)),
        )
      ) {
        noteClaim(claim.id);
      }
    }
  }

  function noteClaim(claimId: string) {
    const claim = claimById.get(claimId);
    if (!claim) return;
    const supported = claim.supportingBeatIds.some((id) => retainedSet.has(id));
    const contradicted = claim.contradictingBeatIds.some((id) => retainedSet.has(id));
    let status: "supported" | "unsupported" | "contradicted" = "unsupported";
    if (contradicted) status = "contradicted";
    else if (supported) status = "supported";
    implied.set(claimId, status);
  }

  const captionClaims = [...implied.entries()].map(([claimId, status]) => ({
    claimId,
    text: claimById.get(claimId)?.statement ?? claimId,
    status,
  }));

  const soundPriming = resolved.sounds.map((s) => {
    const asset = pack.assets.find((a) => a.id === s.assetId);
    const role = s.role;
    let note = "Audio bed under the reel.";
    if (role === "sting") note = "Short sting primes a reaction at that moment.";
    if (role === "ambience") note = "Ambience sets atmosphere without dialogue.";
    if (asset?.tags?.includes("urgency") || /alarm|urgent|suspici/i.test(s.assetId)) {
      note = "Sound primes urgency or suspicion before context lands.";
    }
    return { assetId: s.assetId, role, note };
  });

  const cast: SourceCompareReport["cast"] = [];
  for (const slot of pack.castSlots ?? []) {
    const op = resolved.casts.get(slot.id);
    if (!op) continue;
    const variant = slot.variants.find((v) => v.id === op.variantId);
    cast.push({
      slotId: slot.id,
      variantId: op.variantId,
      variantLabel: variant?.label ?? op.variantId,
      sourceFaithful: Boolean(
        variant?.sourceFaithful || variant?.id === slot.defaultVariantId,
      ),
      disclosureShown: op.showReconstructionDisclosure,
    });
  }

  const look: SourceCompareReport["look"] =
    resolved.look?.treatmentId != null
      ? {
          treatmentId: resolved.look.treatmentId,
          intensityMilli: resolved.look.intensityMilli,
        }
      : null;

  const motions: SourceCompareReport["motions"] = [...resolved.motions.entries()]
    .filter(([, op]) => op.presetId != null)
    .map(([beatId, op]) => ({ beatId, presetId: op.presetId as string }));

  const sfx: SourceCompareReport["sfx"] = resolved.sfx.map((inst) => ({
    id: inst.id,
    assetId: inst.assetId,
    atTick: inst.atTick,
    volumeMilli: inst.volumeMilli,
  }));

  const stickers: SourceCompareReport["stickers"] = resolved.stickers.map((op) => ({
    id: op.id,
    stickerId: op.stickerId,
    startTick: op.startTick,
    endTick: op.endTick,
  }));

  const transitions: SourceCompareReport["transitions"] = [
    ...resolved.transitions.entries(),
  ]
    .filter(([, op]) => op.presetId != null)
    .map(([beatId, op]) => ({
      beatId,
      presetId: op.presetId as string,
      durationTicks: op.durationTicks,
    }));

  return {
    retainedBeats,
    omittedBeats,
    trimmedBeats,
    reordered,
    reorderOrder: [...resolved.beatOrder],
    frameExcludedCarriers,
    captionClaims,
    soundPriming,
    cast,
    look,
    motions,
    sfx,
    stickers,
    transitions,
    showsReconstructionLabel: shouldShowReconstructionLabel(pack, resolved),
  };
}
